import React, {useCallback, useEffect, useRef} from "react";
import {useNavigate, useLocation} from "react-router-dom";
import {
  IconButton,
  Image,
  Avatar,
  Box,
  CloseButton,
  Flex,
  VStack,
  Icon,
  Text,
  Drawer,
  DrawerContent,
  useDisclosure,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  ModalCloseButton,
  Button,
  useBreakpointValue,
  VisuallyHidden,
} from "@chakra-ui/react";
import {FiHome, FiMenu, FiChevronDown, FiUser, FiLock, FiFolder, FiLogOut, FiBookOpen} from "react-icons/fi";
import {HiOutlineDocumentDuplicate} from "react-icons/hi";
import {PiMoonStars} from "react-icons/pi";
import {BsChatDots} from "react-icons/bs";
import {MdOutlineAttachMoney} from "react-icons/md";
import {CiPillsBottle1} from "react-icons/ci";
import {useSocket} from "../additional_files/custom";
import {logoutUser} from "../authRequests";
import {crud, url} from "../crudRequests";
import {createContext} from "react";
import {LuCalendarRange} from "react-icons/lu";
import ProfileSwitcher from "./ProfileSwitcher";
import DependentBanner from "./DependentBanner";
import {isJournalOn} from "../features/journal";
import {usePatientUnreadSummary} from "../features/messaging/hooks";
import {formatBadgeCount, isMessagingBadgeEnabled} from "../features/messaging/unread";
export const SocketContext = createContext(null);

// Which nav item each permission domain corresponds to. The SERVER decides
// which domains a guardian can reach for a given dependent; this only maps
// those answers onto the menu.
//
// What stood here was a hardcoded allow-list of four names. It could not
// express an age band, an expired order or the dependent's own restriction, and
// worse, it was the browser deciding — a hidden section is still a section
// whose data was sent.
const DEPENDENT_SECTION_LINKS = {
  overview: "Home",
  profile: "Profile",
  appointments: "Appointments",
  forms: "Forms",
  billing: "Account",
  documents: "Files",
};

const LinkItems = [
  {name: "Home", route: "../", icon: FiHome},
  {name: "Appointments", route: "../appointments", icon: LuCalendarRange},
  {name: "Forms", route: "../forms", icon: HiOutlineDocumentDuplicate},
  {name: "Sleep Diary", route: "../sleep", icon: PiMoonStars},
  {
    name: "Prescriptions",
    route: "../prescriptionCompliance",
    icon: CiPillsBottle1,
  },
  {name: "Journal", route: "../journal", icon: FiBookOpen},
  {name: "Messaging", route: "../chat", icon: BsChatDots},
  {name: "Files", route: "../files", icon: FiFolder},
  {name: "Account", route: "../account", icon: MdOutlineAttachMoney},
  {name: "Profile", route: "../user", icon: FiUser},
  {name: "Security", route: "../security", icon: FiLock},
];

const PORTAL_TAB_KEY = {
  Appointments: "appointments",
  Forms: "forms",
  "Sleep Diary": "sleepDiary",
  Prescriptions: "prescriptions",
  Journal: "journal",
  Messaging: "messaging",
  Files: "files",
  Account: "billing",
};

const ALWAYS_VISIBLE = new Set(["Home", "Profile", "Security"]);

const SidebarContent = ({state, dispatch, onClose, ...rest}) => {
  const navigate = useNavigate();
  const portalTabs = state?.organization?.patientPortalTabs;
  const viewingDependent = !!state?.viewingDependent;
  // What the server said, for THIS dependent. Absent means nothing is offered,
  // which is the right answer for a guardianship that grants nothing.
  const dependentSections = new Set(state?.activeDependent?.sections || []);
  const dependentLinkNames = new Set(
    [...dependentSections].map((section) => DEPENDENT_SECTION_LINKS[section]).filter(Boolean)
  );

  const visibleLinks = LinkItems.filter((link) => {
    // Only where the clinic turned the journal's companion on (features/journal).
    if (link.name === "Journal" && !isJournalOn(state)) return false;
    if (viewingDependent) return dependentLinkNames.has(link.name);
    if (ALWAYS_VISIBLE.has(link.name)) return true;
    const key = PORTAL_TAB_KEY[link.name];
    if (!key) return true;
    if (!portalTabs) return true;
    return portalTabs[key] !== false;
  });

  // The Messaging badge. Off in the dependent view and when the clinic turned
  // the tab off: nothing is requested for an item that is not offered.
  const messagingBadgeEnabled = isMessagingBadgeEnabled(state);
  const unreadSummary = usePatientUnreadSummary(
    {jwt: state?.jwt},
    {patientId: state?.patient?.pid, enabled: messagingBadgeEnabled}
  );
  const messagingUnread = messagingBadgeEnabled ? unreadSummary.data?.totalUnread || 0 : 0;

  const isActive = (route, name) => {
    const isInPath = window.location.href.includes(route.split("/")[1]);
    return (
      (name !== "Home" && isInPath) ||
      (name === "Home" &&
        window.location.href.split("/")[3]?.length < 2)
    );
  };

  return (
    <Box
      transition="all 0.15s ease"
      bg="white"
      borderRight="1px"
      borderRightColor="#e6e6e6"
      w={{base: "full", md: 60}}
      pos="fixed"
      h="full"
      zIndex={20}
      {...rest}
    >
      <Flex
        direction="column"
        h="full"
        justifyContent="space-between"
      >
        <Flex
          h="16"
          alignItems="center"
          px="5"
          justifyContent="center"
          position="relative"
          borderBottomWidth="1px"
          borderBottomColor="#e6e6e6"
          flexShrink={0}
        >
          <Image src="/favicon.png" alt="Brightlight" boxSize="40px" />
          <CloseButton
            position="absolute"
            right="3"
            top="3"
            display={{base: "flex", md: "none"}}
            onClick={onClose}
          />
        </Flex>

        <ProfileSwitcher state={state} dispatch={dispatch} />

        <VStack
          flex="1"
          overflowY="auto"
          overflowX="hidden"
          px="3"
          py="3"
          spacing="1"
          align="stretch"
          className="sbar3"
        >
          {visibleLinks.map((link) => (
            <NavItem
              key={link.name}
              icon={link.icon}
              isActive={isActive(link.route, link.name)}
              badgeCount={link.name === "Messaging" ? messagingUnread : 0}
              onClose={onClose}
              onClick={() => {
                navigate(link.route, {
                  state: {...state, firstLoading: true},
                });
              }}
            >
              {link.name}
            </NavItem>
          ))}
        </VStack>

        <Box
          px="4"
          py="3"
          borderTop="1px"
          borderTopColor="#e6e6e6"
          flexShrink={0}
          display={{base: "none", md: "block"}}
        >
          <Menu placement="top-start" closeOnBlur={true}>
            <MenuButton w="full" _focus={{boxShadow: "none"}} borderRadius="8px" px="2" py="2" _hover={{bg: "#f8f9fa"}} transition="all 0.15s ease">
              <Flex alignItems="center" justifyContent="space-between" gap="3">
                <Flex alignItems="center" gap="3" minW={0} flex={1}>
                  <Avatar
                    size="sm"
                    name={state?.patient?.fName || "User"}
                    bg="#1a1f36"
                    color="white"
                    fontSize="xs"
                    flexShrink={0}
                  />
                  <VStack alignItems="flex-start" spacing="0" minW={0} flex={1}>
                    <Text fontSize="13px" fontWeight="500" color="#1a1f36" lineHeight="1.3" noOfLines={1} maxW="100%">
                      {state.userType === "relationship"
                        ? state?.relationship?.name
                        : state?.patient?.fName
                        ? state?.patient?.fName + " " + state?.patient?.lName
                        : "No patient name"}
                    </Text>
                    <Text fontSize="12px" color="#6b7280" lineHeight="1.3" noOfLines={1} maxW="100%">
                      {state.userType === "relationship"
                        ? state?.relationship?.email || state?.relationship?.phone
                        : state?.patient?.email ||
                          state?.patient?.phone ||
                          "No contact info"}
                    </Text>
                  </VStack>
                </Flex>
                <Icon as={FiChevronDown} color="#6b7280" fontSize="14px" />
              </Flex>
            </MenuButton>
            <MenuList
              bg="white"
              borderColor="#e6e6e6"
              zIndex={9999}
              minW="200px"
              shadow="lg"
              borderRadius="8px"
              py="1"
            >
              <MenuItem
                onClick={logoutUser}
                fontSize="13px"
                color="#1a1f36"
                _hover={{bg: "#f8f9fa"}}
                icon={<Icon as={FiLogOut} fontSize="14px" color="#6b7280" />}
              >
                Sign out
              </MenuItem>
            </MenuList>
          </Menu>
        </Box>
      </Flex>
    </Box>
  );
};

const NavItem = ({icon, children, isActive, onClose, onClick, badgeCount = 0, ...rest}) => {
  const handleClick = (e) => {
    if (onClick) {
      onClick(e);
    }
    if (onClose && window.innerWidth < 768) {
      setTimeout(() => onClose(), 100);
    }
  };

  return (
    // A BUTTON, not a div with an onClick.
    //
    // This is the portal's primary navigation, and it was a <Flex> — a plain
    // div — carrying `role="group"` and a click handler. Three consequences,
    // all of them on the patient-facing side of the product:
    //
    //   - No keyboard could reach it. Tab moved from the logo straight past
    //     Home, Appointments, Forms, Files, Account and Profile. For anyone who
    //     does not use a mouse, the portal had no navigation.
    //   - `role="group"` told assistive technology these were GROUPINGS, not
    //     controls, so a screen reader announced no way to move around either.
    //   - Nothing said which item was current except its colour.
    //
    // `role="group"` was load-bearing for Chakra's `_groupHover`, so the icon's
    // hover colour moves into the parent's own `_hover` as a descendant rule
    // rather than being dropped.
    <Flex
      as="button"
      type="button"
      w="full"
      textAlign="left"
      align="center"
      px="3"
      py="2.5"
      borderRadius="8px"
      cursor="pointer"
      fontSize="14px"
      transition="all 0.15s ease"
      aria-current={isActive ? "page" : undefined}
      _hover={{
        bg: "#f8f9fa",
        color: "#1a1f36",
        "& svg": {color: "#6772e5"},
      }}
      bg={isActive ? "#f8f9fa" : "transparent"}
      color={isActive ? "#1a1f36" : "#6b7280"}
      fontWeight={isActive ? "600" : "500"}
      onClick={handleClick}
      _focusVisible={{
        boxShadow: "0 0 0 2px #6772e5",
        outline: "none",
      }}
      {...rest}
    >
      {icon && (
        <Icon
          mr="3"
          fontSize="16px"
          color={isActive ? "#6772e5" : "#6b7280"}
          as={icon}
        />
      )}
      {children}
      {badgeCount > 0 && (
        <Box
          as="span"
          ml="auto"
          minW="20px"
          h="20px"
          px="6px"
          borderRadius="full"
          bg="#6772e5"
          color="white"
          fontSize="11px"
          fontWeight="600"
          lineHeight="20px"
          textAlign="center"
          aria-hidden="true"
        >
          {formatBadgeCount(badgeCount)}
        </Box>
      )}
      {badgeCount > 0 && (
        <VisuallyHidden>{badgeCount} unread messages</VisuallyHidden>
      )}
    </Flex>
  );
};

const MobileNav = ({onOpen, state}) => {
  return (
    <Flex
      ml={{base: 0, md: 60}}
      px={{base: 4, md: 4}}
      height="14"
      alignItems="center"
      bg="white"
      borderBottomWidth="1px"
      borderBottomColor="#e6e6e6"
      justifyContent="space-between"
      display={{base: "flex", md: "none"}}
      position="fixed"
      top={0}
      left={0}
      right={0}
      zIndex={10}
    >
      <IconButton
        onClick={onOpen}
        variant="ghost"
        aria-label="open menu"
        icon={<FiMenu />}
        size="sm"
        color="#1a1f36"
        _hover={{bg: "#f8f9fa"}}
      />
      <Image src="/favicon.png" alt="Brightlight" boxSize="32px" />
      <Menu placement="bottom-end">
        <MenuButton
          as={IconButton}
          variant="ghost"
          aria-label="Sign out menu"
          icon={<FiLogOut />}
          size="sm"
          color="#6b7280"
          _hover={{color: "#1a1f36", bg: "#f8f9fa"}}
        />
        <MenuList
          bg="white"
          borderColor="#e6e6e6"
          zIndex={9999}
          minW="150px"
          shadow="lg"
          borderRadius="8px"
          py="1"
        >
          <MenuItem onClick={logoutUser} fontSize="13px" _hover={{bg: "#f8f9fa"}}>Sign out</MenuItem>
        </MenuList>
      </Menu>
    </Flex>
  );
};

const Layout = ({state, children, dispatch}) => {
  const socket = useSocket(url, state.patient?.pid, dispatch, state.db);
  const location = useLocation();
  const isMobile = useBreakpointValue({base: true, md: false});
  const refreshInFlightRef = useRef(false);
  const latestStateRef = useRef(state);

  const {isOpen, onOpen, onClose} = useDisclosure();
  const {
    isOpen: isModalOpen,
    onOpen: onModalOpen,
    onClose: onModalClose,
  } = useDisclosure();

  useEffect(() => {
    const hasSeenModal = localStorage.getItem("blh_mobile_warning_shown");
    const isRootPath = location.pathname === "/" || location.pathname === "";

    if (isRootPath && isMobile && !hasSeenModal) {
      onModalOpen();
    }

  }, [location.pathname, isMobile, onModalOpen]);

  const handleModalClose = () => {
    localStorage.setItem("blh_mobile_warning_shown", "true");
    onModalClose();
  };

  useEffect(() => {
    latestStateRef.current = state;
  }, [state]);

  const refreshLatestPatient = useCallback(async () => {
    const currentState = latestStateRef.current;
    if (
      !dispatch ||
      !currentState?.patient?.pid ||
      !currentState?.db ||
      !currentState?.jwt
    ) {
      return;
    }
    if (refreshInFlightRef.current) return;

    refreshInFlightRef.current = true;
    try {
      const res = await crud(currentState, [
        {
          db: currentState.db,
          collection: "patients",
          parameters: [{pid: currentState.patient.pid}],
          method: "findOne",
        },
      ]);

      const latestPatient = res?.data?.[0];
      if (latestPatient?.pid === currentState.patient.pid) {
        dispatch({
          type: "UPDATE_PATIENT",
          pid: latestPatient.pid,
          payload: latestPatient,
        });
      }
    } catch (error) {
      console.error("Failed to refresh latest patient data:", error);
    } finally {
      refreshInFlightRef.current = false;
    }
  }, [dispatch]);

  useEffect(() => {
    refreshLatestPatient();
  }, [refreshLatestPatient, location.pathname]);

  useEffect(() => {
    const handleFocus = () => {
      refreshLatestPatient();
    };

    const handleVisibility = () => {
      if (document.visibilityState === "visible") {
        refreshLatestPatient();
      }
    };

    window.addEventListener("focus", handleFocus);
    document.addEventListener("visibilitychange", handleVisibility);

    return () => {
      window.removeEventListener("focus", handleFocus);
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [refreshLatestPatient]);

  useEffect(() => {
    if (!state?.patient?.pid) return;

    const intervalId = window.setInterval(() => {
      if (document.visibilityState === "visible") {
        refreshLatestPatient();
      }
    }, 30000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [refreshLatestPatient, state?.patient?.pid]);

  return (
    <SocketContext.Provider value={socket?.current || null}>
      <Box h="100vh" bg="#f9fafb">
        <SidebarContent
          state={state}
          dispatch={dispatch}
          onClose={onClose}
          display={{base: "none", md: "block"}}
        />
        <Drawer
          isOpen={isOpen}
          placement="left"
          onClose={onClose}
          returnFocusOnClose={false}
          onOverlayClick={onClose}
          size="full"
        >
          <DrawerContent>
            <SidebarContent state={state} dispatch={dispatch} onClose={onClose} />
          </DrawerContent>
        </Drawer>
        <MobileNav onOpen={onOpen} state={state} />
        <Box
          ml={{base: 0, md: 60}}
          mt={{base: "56px", md: 0}}
          px={{base: 4, md: 4}}
          pt={0}
          pb={2}
          height={{base: "calc(100vh - 56px)", md: "100vh"}}
          display="flex"
          flexDirection="column"
          overflow="hidden"
          bg="#f9fafb"
        >
          <DependentBanner state={state} dispatch={dispatch} />
          {children}
        </Box>

        <Modal isOpen={isModalOpen} onClose={handleModalClose} isCentered>
          <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(2px)" />
          <ModalContent mx={4} maxW="md" borderRadius="12px">
            <ModalCloseButton />
            <ModalBody py={6} px={6}>
              <VStack spacing={4} align="stretch">
                <Box textAlign="center">
                  <Image
                    src="/favicon.png"
                    alt="Brightlight"
                    boxSize="48px"
                    mx="auto"
                    mb={3}
                  />
                </Box>
                <Text
                  fontSize="lg"
                  fontWeight="600"
                  color="#1a1f36"
                  textAlign="center"
                >
                  For a Better Experience
                </Text>
                <Text
                  fontSize="14px"
                  color="#6b7280"
                  textAlign="center"
                  lineHeight="tall"
                >
                  We recommend accessing this application from a computer for
                  the best experience. The desktop version offers enhanced
                  features and a more comfortable interface.
                </Text>
                <Button
                  bg="#1a1f36"
                  color="white"
                  _hover={{bg: "#2d3348"}}
                  onClick={handleModalClose}
                  mt={2}
                  size="md"
                  borderRadius="8px"
                  fontSize="13px"
                  fontWeight="500"
                >
                  Got it
                </Button>
              </VStack>
            </ModalBody>
          </ModalContent>
        </Modal>
      </Box>
    </SocketContext.Provider>
  );
};

export default Layout;
